import { Key } from 'ts-key-enum';
import { useFileFlippingCallbacks } from 'js/file_viewer/api/navigation';
import { UserActionContext } from 'js/file_viewer/core/logging/constants';
import * as BrowserDetection from 'js/file_viewer/core/utils/browser_detection';
export function isKeyboardEvent(arg) {
    return arg != null && arg.preventDefault != null;
}
function withPreventDefault(func) {
    return function (event) {
        func();
        if (isKeyboardEvent(event)) {
            event.preventDefault();
        }
    };
}
function getFlipLeft({ onFlip, requireCommandKey, fileIndex }) {
    if (fileIndex === 0) {
        return [];
    }
    const callback = withPreventDefault(() => onFlip(UserActionContext.Keyboard));
    const commandFlip = {
        keyboardEventCriteria: {
            key: Key.ArrowLeft,
            osModifierKey: true,
        },
        callback,
    };
    if (requireCommandKey) {
        return [commandFlip];
    }
    return [
        {
            keyboardEventCriteria: {
                key: Key.ArrowLeft,
            },
            callback,
        },
        commandFlip,
    ];
}
function getFlipRight({ onFlip, requireCommandKey, fileIndex, collectionFileCount, }) {
    if (fileIndex === collectionFileCount - 1) {
        return [];
    }
    const callback = withPreventDefault(() => onFlip(UserActionContext.Keyboard));
    const commandFlip = {
        keyboardEventCriteria: {
            key: Key.ArrowRight,
            osModifierKey: true,
        },
        callback: callback,
    };
    if (requireCommandKey) {
        return [commandFlip];
    }
    return [
        {
            keyboardEventCriteria: {
                key: Key.ArrowRight,
            },
            callback: callback,
        },
        commandFlip,
    ];
}
export function useFileFlippingBindings(flipArgs) {
    const { flipToNextFile, flipToPreviousFile, flipToNextSlideshowFile, flipToPreviousSlideshowFile } = useFileFlippingCallbacks();
    return [
        ...getFlipRight({
            ...flipArgs,
            onFlip: flipArgs.isSlideshow ? flipToNextSlideshowFile : flipToNextFile,
        }),
        ...getFlipLeft({
            ...flipArgs,
            onFlip: flipArgs.isSlideshow ? flipToPreviousSlideshowFile : flipToPreviousFile,
        }),
    ];
}
function getPageUp(callback) {
    return {
        keyboardEventCriteria: {
            key: Key.PageUp,
        },
        callback: withPreventDefault(callback),
    };
}
function getPageDown(callback) {
    return {
        keyboardEventCriteria: {
            key: Key.PageDown,
        },
        callback: withPreventDefault(callback),
    };
}
export function getPageUpDownBindings({ pageUp, pageDown }) {
    return [getPageUp(pageUp), getPageDown(pageDown)];
}
function getUp(callback) {
    return {
        keyboardEventCriteria: {
            key: Key.ArrowUp,
        },
        callback: withPreventDefault(callback),
    };
}
function getDown(callback) {
    return {
        keyboardEventCriteria: {
            key: Key.ArrowDown,
        },
        callback: withPreventDefault(callback),
    };
}
export function getUpDownBindings({ up, down }) {
    return [getUp(up), getDown(down)];
}
function getZoomInBindings(callback) {
    return [
        {
            keyboardEventCriteria: {
                // The browser sets the same '=' key on the event when doing both
                // cmd + '=' and cmd + shift + '='.  Since both combinations zoom
                // the browser, we just listen for an event with the '=' key and the
                // modifier key.
                key: '=',
                osModifierKey: true,
            },
            callback: withPreventDefault(callback),
        },
        {
            keyboardEventCriteria: {
                // Similar to above, but some keyboard layouts have '+' as the default key
                key: '+',
                osModifierKey: true,
            },
            callback: withPreventDefault(callback),
        },
    ];
}
function getZoomOutBindings(callback) {
    return [
        {
            keyboardEventCriteria: {
                key: '-',
                osModifierKey: true,
            },
            callback: withPreventDefault(callback),
        },
    ];
}
export function getZoomInOutBindings({ zoomIn, zoomOut }) {
    return [...getZoomInBindings(zoomIn), ...getZoomOutBindings(zoomOut)];
}
function getHomeBinding(callback) {
    return {
        keyboardEventCriteria: {
            key: Key.Home,
        },
        callback: withPreventDefault(callback),
    };
}
function getEndBinding(callback) {
    return {
        keyboardEventCriteria: {
            key: Key.End,
        },
        callback: withPreventDefault(callback),
    };
}
export function getHomeEndBindings({ home, end }) {
    return [getHomeBinding(home), getEndBinding(end)];
}
export function getFindInDocBinding(onFindInDoc) {
    return {
        keyboardEventCriteria: {
            key: 'f',
            osModifierKey: true,
        },
        callback: withPreventDefault(onFindInDoc),
    };
}
export function getBrowserFindBinding(onBrowserFind) {
    return {
        keyboardEventCriteria: {
            key: 'f',
            osModifierKey: true,
        },
        callback: onBrowserFind,
    };
}
export function getEscapeBinding(onEscape) {
    return {
        keyboardEventCriteria: {
            key: Key.Escape,
        },
        callback: withPreventDefault(onEscape),
    };
}
export function getDeleteFileBindings(callback) {
    const deleteKeyboardBinding = getKeyboardBinding({
        key: Key.Delete,
        callback: () => {
            callback(Key.Delete);
        },
    });
    const backspaceKeyboardBinding = getKeyboardBinding({
        key: Key.Backspace,
        callback: () => {
            callback(Key.Backspace);
        },
    });
    if (!BrowserDetection.mac) {
        return [deleteKeyboardBinding];
    }
    return [deleteKeyboardBinding, backspaceKeyboardBinding];
}
export function getKeyboardBinding({ key, callback, shift, osModifierKey, }) {
    return {
        keyboardEventCriteria: {
            key,
            shiftKey: shift || false,
            osModifierKey: osModifierKey || false,
        },
        callback,
    };
}
//# sourceMappingURL=bindings.preenvesnextjs.map
